import {
  useParams,
  Link,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";

import {
  useTheme,
  Button,
  styled,
  Loader,
  Tabs,
  Tab,
} from "@monolith-forensics/monolith-ui";

import {
  Evidence,
  EvidenceContextProvider,
  EvidenceDetailsHeader,
  useEvidenceContext,
  EvidenceActionButton,
  EvidenceDetailsSidePanel,
  CaseProvider,
} from "@/Features";
import {
  caseIdFromSearchParams,
  evidenceDetailPath,
} from "@/Features/Evidence/evidenceLinks";
import { usePermissions } from "@/hooks";
import { addRecentEvidence } from "@/api/evidence/recentEvidence";
import { EvidenceOverview, EvidenceNotes } from "./views";
import { PanelRightCloseIcon, PanelRightOpenIcon } from "lucide-react";

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;

  & .action-menu-item {
    display: flex;
    align-items: center;
    align-content: center;
    margin-right: 12px;
    cursor: pointer;
    border-radius: 5px;
    font-size: 12px;
    &:hover {
      text-decoration: underline;
    }
    & svg {
      font-size: 15px;
      margin-right: 5px;
    }
  }
`;

const renderTabs = (data: Evidence, caseId?: number) => {
  const tabs = [
    {
      value: "overview",
      label: "Overview",
      disabled: false,
      href: evidenceDetailPath(data.uuid, { activeTab: "overview", caseId }),
    },
    {
      value: "notes",
      label: "Notes",
      disabled: false,
      href: evidenceDetailPath(data.uuid, { activeTab: "notes", caseId }),
    },
  ];

  return tabs.map((tab) => (
    <Link key={tab.value} to={tab.href}>
      <Tab value={tab.value} disabled={tab.disabled}>
        {tab.label}
      </Tab>
    </Link>
  ));
};

const EvidenceDetailsContent: React.FC<{}> = () => {
  const theme = useTheme();
  const { currentUser } = usePermissions();
  const { active_tab } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const evidenceContext = useEvidenceContext();
  const [searchParams] = useSearchParams();
  const caseId = caseIdFromSearchParams(searchParams);
  if (!evidenceContext) {
    throw new Error("Evidence context not found");
  }

  const { data: evidenceInfo, isLoading, isFetched, refetch } = evidenceContext;

  // Once the Overview tab has been opened for this item, keep it mounted
  // (hidden) while Notes is open: its Photos tab holds the upload queue, and
  // unmounting it would abort uploads in flight.
  const evidenceUuid = evidenceInfo?.uuid;
  const [overviewVisitedFor, setOverviewVisitedFor] = useState<string>();
  useEffect(() => {
    if (active_tab === "overview" && evidenceUuid) {
      setOverviewVisitedFor(evidenceUuid);
    }
  }, [active_tab, evidenceUuid]);
  const showOverview = active_tab === "overview";
  const mountOverview =
    showOverview || (!!evidenceUuid && overviewVisitedFor === evidenceUuid);

  const handleEditSubmit = () => {
    refetch?.();
  };

  const handleDelete = () => {
    navigate(`/evidence`, { replace: true });
  };

  useEffect(() => {
    if (isFetched) {
      addRecentEvidence({
        evidence_id: evidenceInfo?.evidence_id,
        user_id: currentUser.user_id,
      });
    }
  }, [isFetched]);

  if (isLoading) return <Loader />;

  return (
    <StyledContent>
      {evidenceInfo && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 0,
            flex: "1 1 auto",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 0,
              flex: "1 1 auto",
            }}
          >
            <Helmet>
              <title>Evidence Details | {evidenceInfo.evidence_number}</title>
            </Helmet>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              <EvidenceDetailsHeader data={evidenceInfo} />
              <EvidenceActionButton
                evidenceInfo={evidenceInfo}
                onEditSubmit={handleEditSubmit}
                onDelete={handleDelete}
                size="xxs"
                enabled={isFetched}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                  marginLeft: "auto",
                }}
              >
                <Button
                  variant="outlined"
                  size="xxs"
                  title={sidebarOpen ? "Close Sidebar" : "Open Sidebar"}
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  style={{
                    padding: "2px 4px",
                  }}
                >
                  {sidebarOpen ? (
                    <PanelRightCloseIcon
                      size={16}
                      color={theme.palette.text.primary}
                    />
                  ) : (
                    <PanelRightOpenIcon
                      size={16}
                      color={theme.palette.text.secondary}
                    />
                  )}
                </Button>
              </div>
            </div>
            <Tabs orientation="horizontal" value={active_tab}>
              {renderTabs(evidenceInfo, caseId)}
            </Tabs>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 0,
                flex: "1 1 auto",
                height: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 1 auto",
                }}
              >
                {mountOverview && (
                  <div
                    style={{
                      display: showOverview ? "flex" : "none",
                      flexDirection: "column",
                      flex: "1 1 auto",
                      minHeight: 0,
                    }}
                  >
                    <EvidenceOverview />
                  </div>
                )}
                {active_tab === "notes" && (
                  <EvidenceNotes evidenceInfo={evidenceInfo} />
                )}
              </div>
            </div>
          </div>
          <EvidenceDetailsSidePanel show={sidebarOpen} />
        </div>
      )}
      {!isLoading && !evidenceInfo && (
        <>
          <Helmet>
            <title>Evidence Details | Access Denied</title>
          </Helmet>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              marginTop: 100,
              padding: 15,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 5,
              width: "fit-content",
              margin: "auto",
            }}
          >
            <div style={{ fontSize: 24, fontWeight: "bold" }}>
              You do not have access to this evidence.
            </div>
            <div style={{ color: theme.palette.text.secondary, fontSize: 18 }}>
              Request access from the case lead or Monolith admin.
            </div>
            <Link to="/evidence">
              <Button
                variant="contained"
                color="primary"
                style={{ marginTop: 20 }}
                size="xs"
              >
                Return To Evidence List
              </Button>
            </Link>
          </div>
        </>
      )}
    </StyledContent>
  );
};

export const EvidenceDetailsPage = () => {
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();

  if (!uuid) {
    throw new Error("No evidence UUID provided");
  }

  // The originating case travels in the `case` query param. When present we are in
  // "case" mode (breadcrumb shows the case, back link returns to the case evidence
  // list); when absent we are in global "Evidence" mode. The param is propagated by
  // the evidence links and preserved across sub-tab navigation, so it survives
  // refreshes and shared links without relying on ephemeral router state.
  const caseId = caseIdFromSearchParams(searchParams);

  return (
    <EvidenceContextProvider uuid={uuid}>
      {caseId !== undefined ? (
        <CaseProvider case_id={caseId}>
          <EvidenceDetailsContent />
        </CaseProvider>
      ) : (
        <EvidenceDetailsContent />
      )}
    </EvidenceContextProvider>
  );
};
